<script setup lang="ts">
import { useLienLocal } from "~/composables/useLienLocal";
/**
 * /collecte-dons-association — charte « apple » (Karim 2026-10-18, modèle /cagnotte) sur
 * les briques communes (HeroApple, CarrouselCartesApple, AtoutsApple,
 * TexteMediaApple, CaptureApple…). Header, footer, police et textes
 * inchangés (SEO) ; JSON-LD identiques.
 */
import { TITRE_CARTE_APPLE, TITRE_SECTION_APPLE } from "~/utils/titresApple";
import AppelFinalApple from "~/components/shared/AppelFinalApple.vue";
import PagesLieesApple, { type PageLiee } from "~/components/shared/PagesLieesApple.vue";
import CarrouselCartesApple, { type CarteApple } from "~/components/shared/CarrouselCartesApple.vue";
import { iconeApple } from "~/utils/iconesApple";
const { t } = useI18n({ useScope: "local" });
const localePath = useLocalePath();
const lienLocal = useLienLocal();

const faqItems = [
  {
    question: t('faq.q1.question'),
    answer: t('faq.q1.reponse'),
  },
  {
    question: t('faq.q2.question'),
    answer: t('faq.q2.reponse'),
  },
  {
    question: t('faq.q3.question'),
    answer: t('faq.q3.reponse'),
  },
  {
    question: t('faq.q4.question'),
    answer: t('faq.q4.reponse'),
  },
  {
    question: t('faq.q5.question'),
    answer: t('faq.q5.reponse'),
  },
  {
    question: t('faq.q6.question'),
    answer: t('faq.q6.reponse'),
  },
  // q7 à q10 : reprises des pages fusionnées ici le 2026-11-04
  // (shared/utils/pagesRegroupees.ts) — moyens de paiement, sans site,
  // meilleure méthode, modèle gratuit.
  {
    question: t('faq.q7.question'),
    answer: t('faq.q7.reponse'),
  },
  {
    question: t('faq.q8.question'),
    answer: t('faq.q8.reponse'),
  },
  {
    question: t('faq.q9.question'),
    answer: t('faq.q9.reponse'),
  },
  {
    question: t('faq.q10.question'),
    answer: t('faq.q10.reponse'),
  },
];

// Comparatif Donoor vs HelloAsso (Karim 2026-07-09) — MET DONOOR EN AVANT,
// sur des faits vérifiables. `positive` = colonne Donoor mise en valeur (✓).
const compareRows = [
  {
    label: t('comparatif.ligne1.label'),
    helloasso: t('comparatif.ligne1.helloasso'),
    donoor: t('comparatif.ligne1.donoor'),
    positive: true,
  },
  {
    label: t('comparatif.ligne2.label'),
    helloasso: t('comparatif.ligne2.helloasso'),
    donoor: t('comparatif.ligne2.donoor'),
    positive: true,
  },
  {
    label: t('comparatif.ligne3.label'),
    helloasso: t('comparatif.ligne3.helloasso'),
    donoor: t('comparatif.ligne3.donoor'),
    positive: true,
  },
  {
    label: t('comparatif.ligne4.label'),
    helloasso: t('comparatif.ligne4.helloasso'),
    donoor: t('comparatif.ligne4.donoor'),
    positive: false,
  },
  {
    label: t('comparatif.ligne5.label'),
    helloasso: t('comparatif.ligne5.helloasso'),
    donoor: t('comparatif.ligne5.donoor'),
    positive: false,
  },
];

// Quel outil pour quelle collecte — cartes avec liens internes (maillage SEO).
const collectTools = [
  {
    svg: "/svg/hands-coin.svg",
    bg: "var(--color-main)",
    dark: false,
    title: t('outils.cagnotte.titre'),
    href: "/cagnotte",
    text: t('outils.cagnotte.texte'),
  },
  {
    svg: "/svg/hands-check.svg",
    bg: "#f5c842",
    dark: true,
    title: t('outils.qr.titre'),
    href: "/qr-codes",
    text: t('outils.qr.texte'),
  },
  {
    svg: "/svg/hands-handshak.svg",
    bg: "#131445",
    dark: false,
    title: t('outils.lien.titre'),
    href: "/lien-de-paiement",
    text: t('outils.lien.texte'),
  },
];

useSeoMeta({
  title: t('seo.title'),
  description: t('seo.description'),
  ogTitle: t('seo.ogTitle'),
  ogDescription: t('seo.ogDescription'),
  ogImage: "https://donoor.org/og/og-association.webp",
  ogUrl: "https://donoor.org/collecte-dons-association",
  twitterCard: "summary_large_image",
});

useHead({
  link: [
    {
      rel: "canonical",
      href: "https://donoor.org/collecte-dons-association",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: t('ariane.accueil'),
            item: "https://donoor.org",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: t('ariane.collecteDons'),
            item: "https://donoor.org/collecte-dons-association",
          },
        ],
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        url: "https://donoor.org/collecte-dons-association",
        inLanguage: "fr-FR",
        isPartOf: { "@id": "https://donoor.org/#website" },
        about: [
          { "@type": "Thing", name: t('ariane.collecteDons') },
          { "@type": "Thing", name: t('termes.assocLoi1901') },
          { "@type": "Thing", name: t('topics.mecenat.titre') },
          { "@type": "Thing", name: t('termes.cotisations') },
          { "@type": "Thing", name: t('termes.recuFiscal') },
        ],
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2"] },
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "DefinedTermSet",
        name: t('definedSet.nom'),
        hasDefinedTerm: [
          {
            "@type": "DefinedTerm",
            name: t('topics.recuFiscal.titre'),
            description: t('definies.recu66.description'),
          },
          {
            "@type": "DefinedTerm",
            name: t('topics.mecenat.titre'),
            description: t('definies.mecenat.description'),
          },
          {
            "@type": "DefinedTerm",
            name: t('topics.cotisations.titre'),
            description: t('definies.cotisations.description'),
          },
          {
            "@type": "DefinedTerm",
            name: t('topics.evenements.titre'),
            description: t('definies.evenements.description'),
          },
        ],
      }),
    },
  ],
});

useBreadcrumbSchema([
  { name: t('ariane.accueil'), url: "https://donoor.org" },
  { name: t('ariane.logiciel'), url: "https://donoor.org/logiciel-association" },
  { name: t('ariane.collecteAssociation'), url: "https://donoor.org/collecte-dons-association" },
]);

// Bloc factuel citable (GEO) : rangée de cartes façon apple.com, comme
// /lien-de-paiement (composant partagé CarrouselCartesApple, Karim 2026-11-04).
// Chaque image montre ce dont parle la carte : l'e-mail du reçu fiscal, le
// calcul du mécénat, la carte de membre, une vraie cagnotte d'événement. Chaque
// carte mène à la page de son sujet (reçus fiscaux, adhésion).
const SUJETS_CADRE = [
  { cle: "recuFiscal", href: "/recus-fiscaux", image: "/assets/fiscal/email-recu-mobile.webp", typeImage: "ecran" },
  { cle: "mecenat", href: "/blog/mecenat-entreprise-cadre-juridique-fiscal", image: "/assets/fiscal/calcul-mecenat.webp", typeImage: "ecran" },
  { cle: "cotisations", href: "/adhesion", image: "/billetterie/carte-membre-pdf.webp", typeImage: "photo" },
  { cle: "evenements", href: "/blog/organiser-evenement-collecte-reussi", image: "/images/cagnotte/sum-mobile.webp", typeImage: "ecran" },
] as const;


// Maillage hub → satellites sectoriels (audit maillage 2026-07-25).
// Ce hub relie désormais TOUTES ses pages-cause, avec des ancres = mots-clés
// exacts (pas « Découvrir ») : les satellites deviennent explorables et le
// « jus » du hub se distribue. Bidirectionnel : les satellites remontent déjà.
const sectorPages = [
  { to: "/collecte-dons-association-sportive", label: t('secteurs.sportive.label'), desc: t('secteurs.sportive.desc'), icon: "/svg/hands-trophy.svg" },
  { to: "/collecte-dons-ecole", label: t('secteurs.ecole.label'), desc: t('secteurs.ecole.desc'), icon: "/svg/hands-baby.svg" },
  { to: "/collecte-dons-association-humanitaire", label: t('secteurs.humanitaire.label'), desc: t('secteurs.humanitaire.desc'), icon: "/svg/hands-globe.svg" },
  { to: "/collecte-dons-association-caritative", label: t('secteurs.caritative.label'), desc: t('secteurs.caritative.desc'), icon: "/svg/hands-heart.svg" },
  { to: "/collecte-dons-association-culturelle", label: t('secteurs.culturelle.label'), desc: t('secteurs.culturelle.desc'), icon: "/svg/hands-palette.svg" },
  { to: "/collecte-dons-association-environnement", label: t('secteurs.environnement.label'), desc: t('secteurs.environnement.desc'), icon: "/svg/hands-leaf.svg" },
  { to: "/collecte-dons-association-handicap", label: t('secteurs.handicap.label'), desc: t('secteurs.handicap.desc'), icon: "/svg/hands-check.svg" },
  { to: "/collecte-dons-association-enfants", label: t('secteurs.enfance.label'), desc: t('secteurs.enfance.desc'), icon: "/svg/hands-baby.svg" },
  { to: "/collecte-dons-association-animaux", label: t('secteurs.animaux.label'), desc: t('secteurs.animaux.desc'), icon: "/svg/hands-heart-2.svg" },
  { to: "/collecte-dons-mosquee", label: t('secteurs.mosquee.label'), desc: t('secteurs.mosquee.desc'), icon: "/svg/hands-coin.svg" },
  { to: "/collecte-dons-ramadan", label: t('secteurs.ramadan.label'), desc: t('secteurs.ramadan.desc'), icon: "/svg/hands-money.svg" },
];

const cartesCadre = computed<CarteApple[]>(() =>
  SUJETS_CADRE.map((sujet) => ({
    surtitre: t(`topics.${sujet.cle}.titre`),
    titre: t(`topics.${sujet.cle}.accroche`),
    texte: t(`topics.${sujet.cle}.texte`),
    image: sujet.image,
    typeImage: sujet.typeImage,
    alt: t("topics.illustration", { titre: t(`topics.${sujet.cle}.titre`) }),
    href: lienLocal(sujet.href),
  })),
);
const registerLink = useRegisterLink();
const pagesSecteurs = computed<PageLiee[]>(() =>
  sectorPages.map((s) => ({ title: s.label, description: s.desc, href: lienLocal(s.to), icon: iconeApple(s.icon) })),
);
const liensCroises = computed<PageLiee[]>(() => [
  { title: t("liens.sportives.titre"), description: t("liens.sportives.description"), href: localePath({ path: "/collecte-dons-association-sportive" }), icon: "i-lucide-trophy" },
  { title: t("liens.humanitaires.titre"), description: t("liens.humanitaires.description"), href: localePath({ path: "/collecte-dons-association-humanitaire" }), icon: "i-lucide-globe" },
  { title: t("liens.caritatives.titre"), description: t("liens.caritatives.description"), href: localePath({ path: "/collecte-dons-association-caritative" }), icon: "i-lucide-hand-heart" },
  { title: t("liens.animaux.titre"), description: t("liens.animaux.description"), href: localePath({ path: "/collecte-dons-association-animaux" }), icon: "i-lucide-paw-print" },
  { title: t("liens.enfance.titre"), description: t("liens.enfance.description"), href: localePath({ path: "/collecte-dons-association-enfants" }), icon: "i-lucide-baby" },
  { title: t("liens.ecoles.titre"), description: t("liens.ecoles.description"), href: localePath({ path: "/collecte-dons-ecole" }), icon: "i-lucide-school" },
]);

const racine = ref<HTMLElement | null>(null);
useApparitionAuDefilement(racine);
</script>

<template>
  <div ref="racine" class="min-h-screen bg-white">
    <CollecteHero />

    <!-- Bloc factuel citable (GEO) : rangée de cartes façon apple.com (composant partagé) -->
    <CarrouselCartesApple
      :titre="t('bloc.titre')"
      :cartes="cartesCadre"
      :lien-texte="t('bloc.cta')"
      :lien-href="registerLink"
      lien-register-cta="collecte-dons-association"
    >
      <template #intro>
        <i18n-t keypath="bloc.texte" tag="p" scope="parent">
          <template #reduction>
            <span class="font-bold text-[#1d1d1f]">{{ t("bloc.reduction") }}</span>
          </template>
        </i18n-t>
      </template>
    </CarrouselCartesApple>

    <!-- « Vos dons arrivent de partout » : la carte sombre + vidéo de l'accueil
         (DonsPartout.vue), même place que sur l'accueil : juste avant les fonds
         directs (Karim 2026-11-04). Marge du haut : la section précédente est
         grise, sans elle la carte collerait à la bordure. -->
    <div class="pt-12 lg:pt-16">
      <LazyVisibleDonsPartout hydrate-on-visible />
    </div>

    <!-- Fonds directs : l'argent va sur le compte de l'association -->
    <TexteMediaApple
      :titre="t('fonds.titre')"
      :description="t('fonds.description')"
      :puces="[1, 2, 3].map((n) => ({ titre: t(`fonds.item${n}.titre`), texte: t(`fonds.item${n}.texte`), icone: ['i-lucide-landmark', 'i-lucide-zap', 'i-lucide-shield-check'][n - 1] }))"
      media="gauche"
    >
      <template #media>
        <TelephoneApple image="/billetterie/app-versements.webp" :largeur="662" :hauteur="1447" :alt="t('fonds.alt')" sizes="xs:240px sm:270px" class="mx-auto w-[240px] sm:w-[270px]" />
      </template>
    </TexteMediaApple>

    <!-- Une plateforme complète -->
    <TexteMediaApple :titre="t('plateforme.titre')" :description="t('plateforme.description')" fond="gris">
      <ul class="space-y-5">
        <li class="flex gap-3">
          <UIcon name="i-lucide-qr-code" class="mt-0.5 size-5 shrink-0 text-(--color-main)" aria-hidden="true" />
          <div>
            <p class="text-[16px] font-semibold text-[#1d1d1f]">{{ t("plateforme.item1.titre") }}</p>
            <i18n-t keypath="plateforme.item1.texte" tag="p" scope="parent" class="text-[15px] leading-[1.5] text-[#6e6e73]">
              <template #qrcodes>
                <NuxtLink :to="localePath({ path: '/qr-codes' })" class="font-semibold text-(--color-main) hover:underline">{{ t("plateforme.qrcodes") }}</NuxtLink>
              </template>
              <template #liens>
                <NuxtLink :to="localePath({ path: '/lien-de-paiement' })" class="font-semibold text-(--color-main) hover:underline">{{ t("plateforme.liensPaiement") }}</NuxtLink>
              </template>
            </i18n-t>
          </div>
        </li>
        <li class="flex gap-3">
          <UIcon name="i-lucide-file-check" class="mt-0.5 size-5 shrink-0 text-(--color-main)" aria-hidden="true" />
          <div>
            <p class="text-[16px] font-semibold text-[#1d1d1f]">{{ t("plateforme.item2.titre") }}</p>
            <i18n-t keypath="plateforme.item2.texte" tag="p" scope="parent" class="text-[15px] leading-[1.5] text-[#6e6e73]">
              <template #recus>
                <NuxtLink :to="localePath({ path: '/recus-fiscaux' })" class="font-semibold text-(--color-main) hover:underline">{{ t("plateforme.recusFiscaux") }}</NuxtLink>
              </template>
            </i18n-t>
          </div>
        </li>
        <li class="flex gap-3">
          <UIcon name="i-lucide-bar-chart-3" class="mt-0.5 size-5 shrink-0 text-(--color-main)" aria-hidden="true" />
          <div>
            <p class="text-[16px] font-semibold text-[#1d1d1f]">{{ t("plateforme.item3.titre") }}</p>
            <p class="text-[15px] leading-[1.5] text-[#6e6e73]">{{ t("plateforme.item3.texte") }}</p>
          </div>
        </li>
      </ul>
      <template #media>
        <TelephoneApple image="/billetterie/app-stats.webp" :largeur="665" :hauteur="1450" :alt="t('plateforme.alt')" sizes="xs:240px sm:270px" class="mx-auto w-[240px] sm:w-[270px]" />
      </template>
    </TexteMediaApple>

    <CaptureApple
      :titre="t('tableau.titre')"
      :description="t('tableau.description')"
      image="/assets/images/Admin.webp"
      :largeur="3452"
      :hauteur="1922"
      :alt="t('tableau.alt')"
    />

    <!-- Les outils de collecte, chacun vers sa page produit -->
    <section class="bg-[#f5f5f7] px-4 py-20 sm:px-8 lg:py-28">
      <div class="mx-auto max-w-[1120px]">
        <div class="mb-12 max-w-[820px]">
          <i18n-t keypath="outils.titre" tag="h2" scope="parent" class="pa-reveal mb-5 text-[32px] text-[#1d1d1f] [text-wrap:balance] sm:text-[48px]" :style="TITRE_SECTION_APPLE">
            <template #collecte>
              <span class="text-(--color-main)">{{ t("outils.collecte") }}</span>
            </template>
          </i18n-t>
          <p class="pa-reveal max-w-[680px] text-[17px] font-medium leading-[1.55] text-[#424245] sm:text-[19px]">{{ t("outils.description") }}</p>
        </div>
        <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <NuxtLink
            v-for="(tool, i) in collectTools"
            :key="tool.href"
            :to="lienLocal(tool.href)"
            class="carte-survol pa-reveal group flex flex-col rounded-[28px] bg-white p-8"
            :style="{ '--d': `${(i % 3) * 80}ms` }"
          >
            <span class="mb-6 flex size-12 items-center justify-center rounded-2xl bg-[#f5f5f7] text-[#1d1d1f] transition-colors group-hover:bg-(--color-main) group-hover:text-white">
              <UIcon :name="iconeApple(tool.svg)" class="size-6" aria-hidden="true" />
            </span>
            <h3 class="mb-3 text-[21px] text-[#1d1d1f]" :style="TITRE_CARTE_APPLE">{{ tool.title }}</h3>
            <p class="mb-6 text-[15px] leading-[1.6] text-[#424245]">{{ tool.text }}</p>
            <span class="mt-auto inline-flex items-center gap-1 text-[15px] font-semibold text-(--color-main)">
              {{ t("commun.enSavoirPlus") }}<UIcon name="i-lucide-chevron-right" class="size-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </NuxtLink>
        </div>
      </div>
    </section>

    <ComparatifDonoorHelloasso
      :rows="compareRows"
      :title="t('comparatif.titre')"
      :title-highlight="t('comparatif.titreHighlight')"
      :subtitle="t('comparatif.sousTitre')"
      :transparent-bg="true"
    />

    <!-- Règles des autres pays ouverts (analyse GEO 01/11/2026). -->
    <DansVotrePays sujet="recus" />

    <FaqSectionApple :items="faqItems" />

    <!-- Une page par type d'association -->
    <PagesLieesApple :titre="t('maillage.titre')" :sous-titre="t('maillage.description')" :pages="pagesSecteurs" />

    <PagesLieesApple :titre="t('liens.titre')" :sous-titre="t('liens.sousTitre')" :pages="liensCroises" class="!pt-0" />

    <AppelFinalApple />
  </div>
</template>

<style scoped>
.carte-survol {
  transition:
    transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1),
    box-shadow 0.4s ease;
}
@media (hover: hover) {
  .carte-survol:hover {
    transform: translateY(-3px);
    box-shadow: 0 24px 50px -30px rgba(0, 0, 0, 0.3);
  }
}
</style>

<i18n lang="json" src="../../i18n/pages/pages/collecte-dons-association.json"></i18n>
